import { withAlpha, useAccentFn } from "@/lib/color";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

function fmtMln(n) {
    if (!n) return "0";
    return `€${(n / 1_000_000).toFixed(0)}mln`;
}

const KIND_LABELS = {
    european: "Coppe Europee",
    national: "Coppe Nazionali",
    supercup: "Supercoppe",
};

const KIND_ORDER = ["european", "national", "supercup"];

export default function Coppe() {
    const [cups, setCups] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/cups");
                setCups(data || []);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const byKind = {};
    for (const c of cups) {
        (byKind[c.kind] = byKind[c.kind] || []).push(c);
    }
    // Within each kind, the backend already sorts by sort_order, so we just keep that order

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="coppe-page">
            <div className="op-label" style={{ color: "rgb(var(--tc-ff7a1a))" }}>// CUPS & TROPHIES</div>
            <h1 className="op-heading text-5xl sm:text-6xl mt-3">
                <span className="op-gradient-text">COPPE</span>
            </h1>
            <p className="text-t8f95a3 text-sm mt-3 max-w-2xl">
                12 trofei in palio: 3 coppe europee, 4 coppe nazionali, 5 supercoppe.
                Tabelloni in attesa delle squadre qualificate.
            </p>

            {loading ? (
                <div className="op-label text-t8f95a3 animate-pulse mt-10">// CARICAMENTO…</div>
            ) : (
                <div className="mt-10 space-y-10">
                    {KIND_ORDER.map((k) => (
                        byKind[k] && (
                            <section key={k}>
                                <div className="op-label mb-3">// {KIND_LABELS[k].toUpperCase()}</div>
                                <div className={
                                    k === "supercup"
                                        ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
                                        : "grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
                                }>
                                    {byKind[k].map((c) => (
                                        <CupCard key={c.slug} cup={c} />
                                    ))}
                                </div>
                            </section>
                        )
                    ))}
                </div>
            )}
        </div>
    );
}

// Trophy hero images: European cups + 4 national cups (WebP, ~150KB each)
const TROPHY_IMAGES = {
    "champions-league-op": "/cups/champions.webp",
    "europa-league-op": "/cups/europa.webp",
    "conference-league-op": "/cups/conference.webp",
    "coppa-italia-op": "/cups/coppa-italia.webp",
    "copa-del-rey-op": "/cups/copa-del-rey.webp",
    "fa-cup-op": "/cups/fa-cup.webp",
    "coupe-de-france-op": "/cups/coupe-de-france.webp",
};

// Lighter, more compact trophy images for the 5 supercups (WebP, ~50-70KB)
const SUPERCUP_IMAGES = {
    "supercoppa-italiana-op": "/cups/supercoppa-italiana.webp",
    "supercopa-espana-op": "/cups/supercopa-espana.webp",
    "community-shield-op": "/cups/community-shield.webp",
    "trophee-des-champions-op": "/cups/trophee-des-champions.webp",
    "supercoppa-europea-op": "/cups/supercoppa-europea.webp",
};

function CupCard({ cup }) {
    const accent = useAccentFn()(cup.accent_color);
    const trophyImg = TROPHY_IMAGES[cup.slug];
    const superImg = SUPERCUP_IMAGES[cup.slug];

    if (superImg) {
        // COMPACT LAYOUT for supercups: smaller trophy showcase (1-match competitions)
        return (
            <Link
                to={`/coppe/${cup.slug}`}
                className="op-card relative block overflow-hidden p-0 transition-all duration-300 hover:scale-[1.02] hover:border-t2bb6ff group"
                style={{ borderTop: `3px solid ${accent}` }}
                data-testid={`cup-card-${cup.slug}`}
            >
                {/* Compact trophy area - square aspect for lighter feel */}
                <div
                    className="relative aspect-square flex items-center justify-center overflow-hidden"
                    style={{
                        background: `radial-gradient(ellipse at center, ${withAlpha(accent, "22")} 0%, ${withAlpha(accent, "06")} 45%, rgb(var(--tc-0a0b0e)) 100%)`,
                    }}
                >
                    {/* Subtle glow */}
                    <div
                        aria-hidden
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background: `radial-gradient(circle at center, ${withAlpha(accent, "35")} 0%, transparent 60%)`,
                            filter: "blur(14px)",
                            opacity: 0.45,
                        }}
                    />
                    {/* Flag corner mark */}
                    <div
                        aria-hidden
                        className="absolute top-2 right-2 text-2xl opacity-70 z-20"
                    >
                        {cup.flag}
                    </div>
                    {/* Trophy image - smaller than European cups */}
                    <img
                        src={superImg}
                        alt={cup.name}
                        loading="lazy"
                        className="relative z-10 max-h-[78%] max-w-[68%] object-contain drop-shadow-[0_6px_20px_rgba(0,0,0,0.55)] group-hover:scale-105 transition-transform duration-500"
                        style={{
                            filter: `drop-shadow(0 0 12px ${withAlpha(accent, "55")})`,
                        }}
                    />
                    {/* Bottom fade */}
                    <div className="absolute inset-x-0 bottom-0 h-16 pointer-events-none bg-gradient-to-t from-t0a0b0e to-transparent" />
                </div>

                {/* Compact info footer */}
                <div className="relative px-4 py-3 border-t border-t22252a bg-t0a0b0e">
                    <div className="flex items-start justify-between gap-2">
                        <div className="op-heading text-sm leading-tight truncate flex-1" style={{ color: accent }}>
                            {cup.name}
                        </div>
                        <div className="op-mono text-[10px] text-t8f95a3 shrink-0">
                            {cup.participants_count} sq.
                        </div>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                        <div className="op-mono text-[11px] text-t8f95a3">
                            Vincitore <span className="text-tffffff font-bold">{fmtMln(cup.prize_winner)}</span>
                        </div>
                        <div className="op-mono text-[11px] font-bold" style={{ color: accent }}>
                            {fmtMln(cup.montepremi)}
                        </div>
                    </div>
                </div>
            </Link>
        );
    }

    if (trophyImg) {
        // SPECIAL LAYOUT for European cups: hero with big trophy + glow
        return (
            <Link
                to={`/coppe/${cup.slug}`}
                className="op-card relative block overflow-hidden p-0 transition-all duration-300 hover:scale-[1.02] hover:border-t2bb6ff group"
                style={{ borderTop: `4px solid ${accent}` }}
                data-testid={`cup-card-${cup.slug}`}
            >
                {/* Trophy showcase area */}
                <div
                    className="relative aspect-[4/5] flex items-center justify-center overflow-hidden"
                    style={{
                        background: `radial-gradient(ellipse at center, ${withAlpha(accent, "30")} 0%, ${withAlpha(accent, "08")} 40%, rgb(var(--tc-0a0b0e)) 100%)`,
                    }}
                >
                    {/* Glow halo */}
                    <div
                        aria-hidden
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background: `radial-gradient(circle at center, ${withAlpha(accent, "50")} 0%, transparent 55%)`,
                            filter: "blur(20px)",
                            opacity: 0.5,
                        }}
                    />
                    {/* Stadium spotlight beams */}
                    <div
                        aria-hidden
                        className="absolute inset-0 pointer-events-none opacity-30"
                        style={{
                            background: `linear-gradient(135deg, transparent 30%, ${withAlpha(accent, "22")} 50%, transparent 70%)`,
                        }}
                    />
                    {/* Trophy image */}
                    <img
                        src={trophyImg}
                        alt={cup.name}
                        className="relative z-10 max-h-[88%] max-w-[78%] object-contain drop-shadow-[0_8px_30px_rgba(0,0,0,0.65)] group-hover:scale-105 transition-transform duration-500"
                        style={{
                            filter: `drop-shadow(0 0 18px ${withAlpha(accent, "66")})`,
                        }}
                    />
                    {/* Bottom fade for legibility */}
                    <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none bg-gradient-to-t from-t0a0b0e to-transparent" />
                </div>

                {/* Info footer */}
                <div className="relative p-5 border-t border-t22252a bg-t0a0b0e">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="op-heading text-xl leading-tight truncate" style={{ color: accent }}>
                                {cup.name}
                            </div>
                            <p className="text-[11px] text-t8f95a3 mt-1 leading-relaxed line-clamp-2">
                                {cup.description}
                            </p>
                        </div>
                        <div className="op-mono text-[10px] text-t8f95a3 shrink-0 mt-1">
                            {cup.participants_count} sq.
                        </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-t22252a">
                        <div>
                            <div className="op-label text-[9px]">Montepremi</div>
                            <div className="op-mono text-sm font-bold" style={{ color: accent }}>
                                {fmtMln(cup.montepremi)}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="op-label text-[9px]">Vincitore</div>
                            <div className="op-mono text-sm text-tffffff">{fmtMln(cup.prize_winner)}</div>
                        </div>
                    </div>
                </div>
            </Link>
        );
    }

    // STANDARD LAYOUT for other cups (national, supercups)
    return (
        <Link
            to={`/coppe/${cup.slug}`}
            className="op-card relative p-5 transition-all hover:scale-[1.02] hover:border-t2bb6ff block overflow-hidden"
            style={{ borderLeft: `4px solid ${accent}` }}
            data-testid={`cup-card-${cup.slug}`}
        >
            {/* Decorative bg: large faded flag + accent radial gradient */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: `radial-gradient(circle at top right, ${withAlpha(accent, "26")} 0%, transparent 60%)`,
                }}
            />
            <div
                aria-hidden
                className="absolute -right-6 -bottom-10 text-[140px] leading-none opacity-[0.08] pointer-events-none select-none"
            >
                {cup.flag}
            </div>

            <div className="relative">
                <div className="flex items-center justify-between">
                    <div className="text-3xl drop-shadow-[0_0_12px_rgba(255,255,255,0.15)]">{cup.flag}</div>
                    <div className="op-mono text-[10px] text-t8f95a3">
                        {cup.participants_count} squadre
                    </div>
                </div>
                <div className="op-heading text-xl mt-3">{cup.name}</div>
                <p className="text-xs text-tc9ccd3 mt-2 leading-relaxed">
                    {cup.description}
                </p>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-t22252a">
                    <div>
                        <div className="op-label text-[9px]">Montepremi</div>
                        <div className="op-mono text-sm font-bold" style={{ color: accent }}>
                            {fmtMln(cup.montepremi)}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="op-label text-[9px]">Vincitore</div>
                        <div className="op-mono text-sm text-tffffff">{fmtMln(cup.prize_winner)}</div>
                    </div>
                </div>
            </div>
        </Link>
    );
}
